<template>
  <div class="musubi-page musubi-page--tr">
    <header class="musubi-page__head">
      <h1>Issue routing</h1>
      <p class="text-muted">
        Where "Report an issue" reports are raised. Every workspace's reports flow into
        Apexl's own Town Centre via its ticket ingest. Set the base URL, the ingest token
        (encrypted at rest, never shown back in full), and which project tickets land in.
      </p>
    </header>

    <div v-if="loading" class="text-muted">Loading…</div>
    <div v-else-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <template v-else>
      <MusubiSectionCard title="Town Centre connection" icon="plug" class="tr-card-gap">
        <template #actions>
          <span v-if="configured" class="tr-status tr-status--ok"><CygnusIcon name="check-circle" class="me-1" />Configured</span>
          <span v-else class="tr-status tr-status--off">Not configured</span>
        </template>

        <div class="tr-field">
          <div class="tr-field__head"><span class="tr-field__label">TC base URL</span></div>
          <input v-model="form.base_url" type="text" class="form-control form-control-sm" placeholder="https://your-tc.apexlstudios.com" >
          <p class="tr-field__help">The base URL of the Town Centre install. Reports post to {base}/api/v1/ingest/tickets.</p>
        </div>

        <div class="tr-field">
          <div class="tr-field__head">
            <span class="tr-field__label">Ingest token</span>
            <span v-if="tokenSet" class="tr-field__hint">set</span>
          </div>
          <input
            v-model="form.ingest_token"
            type="password"
            autocomplete="new-password"
            class="form-control form-control-sm"
            :placeholder="tokenMasked || 'Tickets ingest token'"
            :disabled="clearToken"
          >
          <p class="tr-field__help">Sent as the X-Ingest-Token header. Leave blank to keep the current token.</p>
          <label v-if="tokenSet" class="tr-clear">
            <input v-model="clearToken" type="checkbox" > Remove the stored token
          </label>
        </div>

        <div class="tr-actions">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="testing" @click="test">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="testing" />Test connection
          </button>
          <div v-if="testResult" :class="['tr-actions__msg', testResult.ok ? 'is-ok' : 'is-error']">{{ testResult.message }}</div>
        </div>
      </MusubiSectionCard>

      <MusubiSectionCard title="Project routing" icon="diagram" class="tr-card-gap"
                         subtitle="Which Town Centre project tickets land in">
        <div class="tr-field">
          <div class="tr-field__head"><span class="tr-field__label">Default project id</span></div>
          <input v-model="form.default_project_id" type="text" inputmode="numeric" class="form-control form-control-sm" placeholder="e.g. 12 (blank = untriaged)" >
          <p class="tr-field__help">The project every report goes to unless a severity override applies. Blank leaves tickets untriaged in Town Centre.</p>
        </div>

        <div class="tr-field">
          <div class="tr-field__head"><span class="tr-field__label">Per-severity overrides</span></div>
          <p class="tr-field__help">Optional. Route a severity to a different project. Blank = use the default.</p>
          <div v-for="sev in severities" :key="sev" class="tr-override-row">
            <span class="tr-override-row__sev">{{ sevLabel(sev) }}</span>
            <input v-model="form.project_overrides[sev]" type="text" inputmode="numeric" class="form-control form-control-sm" placeholder="project id" >
          </div>
        </div>
      </MusubiSectionCard>

      <MusubiSectionCard title="Direct Mail fulfilment" icon="envelope" class="tr-card-gap"
                         subtitle="Where a submitted Direct Mail batch is emailed for pricing and dispatch">
        <template #actions>
          <span v-if="dmOpsConfigured" class="tr-status tr-status--ok"><CygnusIcon name="check-circle" class="me-1" />Configured</span>
          <span v-else class="tr-status tr-status--off">Not configured</span>
        </template>
        <div class="tr-field">
          <div class="tr-field__head"><span class="tr-field__label">Ops fulfilment address</span></div>
          <input v-model="form.direct_mail_ops_email" type="email" autocomplete="off" class="form-control form-control-sm" placeholder="print-ops@apexlstudios.com" >
          <p class="tr-field__help">Submitted batches email the recipient list and artwork here for a human to price and dispatch. Blank means submissions are held with a "needs config" note and no email is sent.</p>
        </div>
      </MusubiSectionCard>

      <MusubiSectionCard title="Email test mode" icon="shield" class="tr-card-gap"
                         subtitle="Platform-wide safety gate - stops outbound email reaching anyone outside the listed domains">
        <template #actions>
          <span v-if="testModeEnabled" class="tr-status tr-status--on"><CygnusIcon name="shield" class="me-1" />On</span>
          <span v-else class="tr-status tr-status--off">Off</span>
        </template>

        <div v-if="testModeEnabled" class="tr-testmode-banner">
          <CygnusIcon name="shield" class="tr-testmode-banner__ic" />
          <div>
            <div class="tr-testmode-banner__title">TEST MODE ON - email only reaches: {{ testModeDomains.join(', ') }}</div>
            <div class="tr-testmode-banner__sub">
              Every other recipient is blocked before delivery and the message is marked failed
              with the reason shown in the campaign and queue views. Nothing is suppressed, and
              no bounce or unsubscribe is recorded.
            </div>
          </div>
        </div>
        <div v-else class="tr-testmode-off">
          Test mode is off - email sends normally to every recipient.
        </div>

        <div v-if="testModeMalformed" class="alert alert-warning py-2 small mt-2 mb-0">
          The stored value could not be read as a list of domains, so the gate is running
          fail-closed on {{ testModeDomains.join(', ') }} only. Save a clean list to fix it.
        </div>

        <div class="tr-field mt-3">
          <div class="tr-field__head"><span class="tr-field__label">Allowed recipient domains</span></div>
          <input
            v-model="form.email_test_mode_allowlist"
            type="text"
            autocomplete="off"
            class="form-control form-control-sm"
            placeholder="apexlstudios.com"
          >
          <p class="tr-field__help">
            Comma-separated domains, for example apexlstudios.com, example.com. Matching is exact -
            a subdomain such as mail.apexlstudios.com is not covered unless you list it too.
            Leave blank to turn test mode off and send normally.
          </p>
        </div>
      </MusubiSectionCard>

      <div class="tr-save">
        <button type="button" class="btn btn-primary btn-sm" :disabled="saving" @click="save">
          <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="saving" /><span v-else>Save</span>
        </button>
        <div v-if="saveMessage" :class="['tr-save__msg', saveOk ? 'is-ok' : 'is-error']">{{ saveMessage }}</div>
      </div>
    </template>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

const ENDPOINT = '/api/v1/admin/integrations/ticket-routing';

export default {
  name: 'AdminTicketRouting',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      loading: true,
      saving: false,
      testing: false,
      error: null,
      configured: false,
      tokenSet: false,
      tokenMasked: '',
      clearToken: false,
      dmOpsConfigured: false,
      testModeEnabled: false,
      testModeDomains: [],
      testModeMalformed: false,
      severities: ['critical', 'high', 'medium', 'low'],
      form: {
        base_url: '',
        ingest_token: '',
        default_project_id: '',
        project_overrides: { critical: '', high: '', medium: '', low: '' },
        direct_mail_ops_email: '',
        email_test_mode_allowlist: '',
      },
      testResult: null,
      saveMessage: '',
      saveOk: false,
    };
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api.get(ENDPOINT);
        const p = res?.data ?? res;
        this.configured = !!p.configured;
        this.tokenSet = !!p.token_set;
        this.tokenMasked = p.token_masked || '';
        this.dmOpsConfigured = !!p.direct_mail_ops_configured;
        this.testModeEnabled = !!p.email_test_mode_enabled;
        this.testModeDomains = p.email_test_mode_domains || [];
        this.testModeMalformed = !!p.email_test_mode_malformed;
        this.severities = p.severities || this.severities;
        const overrides = { critical: '', high: '', medium: '', low: '' };
        Object.entries(p.project_overrides || {}).forEach(([sev, pid]) => { overrides[sev] = String(pid); });
        this.form = {
          base_url: p.base_url || '',
          ingest_token: '',
          default_project_id: p.default_project_id != null ? String(p.default_project_id) : '',
          project_overrides: overrides,
          direct_mail_ops_email: p.direct_mail_ops_email || '',
          email_test_mode_allowlist: p.email_test_mode_raw || '',
        };
        this.clearToken = false;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to load issue routing.';
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      this.saveMessage = '';
      this.saveOk = false;
      const body = {
        base_url: this.form.base_url || '',
        default_project_id: this.form.default_project_id || '',
        project_overrides: {},
        direct_mail_ops_email: (this.form.direct_mail_ops_email || '').trim(),
        email_test_mode_allowlist: (this.form.email_test_mode_allowlist || '').trim(),
      };
      this.severities.forEach((sev) => {
        const v = (this.form.project_overrides[sev] || '').trim();
        if (v) body.project_overrides[sev] = v;
      });
      if (this.clearToken) body.clear_token = true;
      else if (this.form.ingest_token) body.ingest_token = this.form.ingest_token;

      try {
        const res = await this.api.put(ENDPOINT, body);
        const payload = res?.data ?? res;
        if (payload?.success) {
          this.saveOk = true;
          this.saveMessage = 'Saved.';
          await this.load();
        } else {
          this.saveMessage = payload?.error || 'Save failed.';
        }
      } catch (e) {
        this.saveMessage = e?.response?.data?.error || e?.message || 'Save failed.';
      } finally {
        this.saving = false;
      }
    },
    async test() {
      this.testing = true;
      this.testResult = null;
      try {
        const res = await this.api.post(`${ENDPOINT}/test`, {});
        const p = res?.data ?? res;
        this.testResult = { ok: !!p.ok, message: p.message || (p.ok ? 'Connected.' : 'Failed.') };
      } catch (e) {
        this.testResult = { ok: false, message: e?.response?.data?.message || e?.message || 'Test failed.' };
      } finally {
        this.testing = false;
      }
    },
    sevLabel(sev) { return sev.charAt(0).toUpperCase() + sev.slice(1); },
  },
};
</script>

<style scoped>
.musubi-page--tr { padding: var(--m-space-6, 32px); max-width: 720px; }
.tr-card-gap { margin-top: 18px; }

.tr-status { font-size: 0.76rem; font-weight: 600; }
.tr-status--ok { color: var(--m-status-success, #10B981); }
.tr-status--off { color: var(--m-text-tertiary); }
.tr-status--on { color: var(--m-status-warning, #D97706); }

.tr-testmode-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  border: 1px solid var(--m-status-warning, #D97706);
  border-radius: 10px;
  background: #FEF6E7;
  padding: 0.7rem 0.9rem;
}
.tr-testmode-banner__ic { color: var(--m-status-warning, #D97706); margin-top: 2px; }
.tr-testmode-banner__title { font-size: 0.86rem; font-weight: 600; color: var(--m-text-primary); }
.tr-testmode-banner__sub { font-size: 0.76rem; color: var(--m-text-secondary); margin-top: 2px; }
.tr-testmode-off { font-size: 0.82rem; color: var(--m-text-secondary); }

.tr-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; }
.tr-field:last-child { margin-bottom: 0; }
.tr-field__head { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.tr-field__label { font-size: 0.85rem; font-weight: 500; color: var(--m-text-primary); }
.tr-field__hint { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); }
.tr-field__help { font-size: 0.76rem; color: var(--m-text-secondary); margin: 2px 0 0; }

.tr-clear { display: flex; align-items: center; gap: 6px; font-size: 0.78rem; color: var(--m-text-secondary); margin-top: 4px; }
.tr-clear input { margin: 0; }

.tr-override-row { display: flex; align-items: center; gap: 10px; margin-top: 8px; }
.tr-override-row__sev { flex: 0 0 90px; font-size: 0.82rem; color: var(--m-text-primary); }
.tr-override-row .form-control { max-width: 180px; }

.tr-actions { display: flex; align-items: center; gap: 10px; margin-top: 12px; }
.tr-actions__msg { font-size: 0.82rem; }
.tr-actions__msg.is-ok { color: var(--m-status-success, #10B981); }
.tr-actions__msg.is-error { color: var(--m-status-danger, #EF4444); }

.tr-save { display: flex; align-items: center; gap: 10px; margin-top: 18px; }
.tr-save__msg { font-size: 0.85rem; }
.tr-save__msg.is-ok { color: var(--m-status-success, #10B981); }
.tr-save__msg.is-error { color: var(--m-status-danger, #EF4444); }
</style>
